<script lang="ts" setup>
/*
 *
 *
 * IMPORTS
 *
 * */

// PRIMEVUE
import PInputText from 'primevue/inputtext'
import PInputNumber from 'primevue/inputnumber'
import PButton from 'primevue/button'
import PBadge from 'primevue/badge'
import PMessage from 'primevue/message'
// VUE, VUE ROUTER, ETC...
import { computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'

// Custom components and types
import type { LearningGrain } from '@/core/domain/learning/grain/LearningGrain.ts'
import type { LearningModuleInput } from '@/core/domain/learning/module/LearningModule.ts'
import { LearningStatus } from '@/core/domain/resources/enums/LearningStatus.ts'
import type { Quiz } from '@/core/domain/quiz/Quiz.ts'
import CLearningModuleGrainSupportList from '@/presentation/components/learning/module/CLearningModuleGrainSupportList.vue'
import CQuizVersionTag from '@/presentation/components/quiz/CQuizVersionTag.vue'
import { useLearningGrainsStore } from '@/presentation/stores/learning/learningGrains.ts'
import { QuizType } from '@/core/domain/resources/enums/QuizType.ts'
import { useConfirm } from 'primevue/useconfirm'
import { useLearningGrainAccessUtils } from '@/presentation/composables/learning/useLearningAccessUtils.ts'
import { storeToRefs } from 'pinia'

// Stores & services
const confirm = useConfirm()
const router = useRouter()
const learningGrainsStore = useLearningGrainsStore()

// Store Refs
const { loading } = storeToRefs(learningGrainsStore)

// Composables
const { canPublishGrain, canEditGrain, canDeleteGrain } = useLearningGrainAccessUtils()

const learningGrain = defineModel<LearningGrain>('learningGrain', {
  required: true
})
const learningModule = defineModel<LearningModuleInput>('learningModule', {
  required: true
})
const fieldToUpdate = defineModel<string | null>('fieldToUpdate', {
  required: true
})

//EMITS
const emits = defineEmits([
  'cancel',
  'delete',
  'start-grain-update',
  'validate',
  'publish',
  'update'
])

// PROPS
const props = defineProps<{
  learningCourseId: number
}>()

// DATA

// COMPUTED
const isEditing = computed(() => {
  return fieldToUpdate.value === `grain_${learningGrain.value.order}`
})

const quizPreQuestions = computed(() => {
  if (learningGrain.value.quizPre) {
    return getQuizCompletion(learningGrain.value.quizPre, learningGrain.value.quizPreQuestionCount)
  }
  return { text: '0', color: 'danger' }
})

const quizPostQuestions = computed(() => {
  if (learningGrain.value.quizPost) {
    return getQuizCompletion(
      learningGrain.value.quizPost,
      learningGrain.value.quizPostQuestionCount
    )
  }
  return { text: '0', color: 'danger' }
})

const getQuizCompletion = (quiz: Quiz, questionCount: number) => {
  const activeVersion = quiz.activeVersion
  if (!activeVersion) {
    return { text: '0', color: 'danger' }
  }

  const count = questionCount
  if (count === 0) {
    return { text: '0', color: 'danger' }
  } else if (count < 5) {
    return { text: count.toString(), color: 'warning' }
  } else {
    return { text: count.toString(), color: 'success' }
  }
}

const learningGrainSupports = computed(() => {
  return learningGrain.value.learningSupports
})

const missingQuizzesText = computed(() => {
  const messages = learningGrain.value.getQuizValidationMessages()

  if (messages.length === 0) {
    return ''
  }

  return messages.join(' ')
})

const canPublish = computed(() => {
  return learningGrain.value.canBePublished
})

const isLoading = computed(() => {
  return loading.value.createLearningGrain || loading.value.updateLearningGrain
})

const missingQuizzesTextList = computed(() => {
  const texts: string[] = []

  if (!learningGrain.value.quizPre || !learningGrain.value.quizPost) {
    texts.push('Le grain doit avoir été enregistré au moins une fois.')
    return texts
  }

  const messages = learningGrain.value.getQuizValidationMessages()
  texts.push(...messages)

  if (learningGrain.value.learningSupports?.length === 0) {
    texts.push('Le grain doit contenir au moins un support pour publier le grain.')
  }

  return texts
})

// METHODS
async function validate() {
  try {
    if (learningGrain.value.id === 0) {
      const res = await learningGrainsStore.createGrain(
        learningGrain.value,
        learningModule.value.id,
        props.learningCourseId
      )
      learningGrain.value.id = res.id
      learningGrain.value.quizPre = res.quizPre
      learningGrain.value.quizPost = res.quizPost
      if (learningModule.value.learningGrains) {
        const index = learningModule.value?.learningGrains?.findIndex(
          (g) => g.order === learningGrain.value.order
        )
        if (index >= 0) learningModule.value.learningGrains[index] = learningGrain.value
      }
    } else {
      learningGrain.value = await learningGrainsStore.updateGrain(
        learningGrain.value,
        learningModule.value.id,
        props.learningCourseId
      )
    }
  } catch (error) {
    console.error('Error updating grain:', error)
  } finally {
    fieldToUpdate.value = null
  }
}

function editQuiz(type: 'pre' | 'post', version: number | undefined = undefined) {
  if (learningGrain.value.quizPre && learningGrain.value.quizPost)
    switch (type) {
      case 'pre':
        router.push({
          name: 'learning-grain-update-quiz',
          query: {
            quizType: QuizType.Values.PRE,
            version
          },
          params: {
            grainId: learningGrain.value.id,
            moduleId: learningModule.value.id,
            courseId: props.learningCourseId,
            quizId: learningGrain.value.quizPre.id
          }
        })
        break
      case 'post':
        router.push({
          name: 'learning-grain-update-quiz',
          query: {
            quizType: QuizType.Values.POST,
            version
          },
          params: {
            grainId: learningGrain.value.id,
            moduleId: learningModule.value.id,
            courseId: props.learningCourseId,
            quizId: learningGrain.value.quizPost.id
          }
        })
        break
    }
}

function startGrainUpdate() {
  fieldToUpdate.value = `grain_${learningGrain.value.order}`
}

async function publishGrain() {
  try {
    learningGrain.value = await learningGrainsStore.updateGrainStatus(
      LearningStatus.Values.ACTIVE,
      learningGrain.value.id,
      learningModule.value.id,
      props.learningCourseId
    )
  } catch (error) {
    console.error('Error publishing grain:', error)
  }
}

async function deleteGrain() {
  confirm.require({
    message: 'Êtes-vous sûr de vouloir supprimer ce grain ?',
    header: 'Confirmation',
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: 'Oui',
    rejectLabel: 'Non',
    accept: async () => {
      try {
        await learningGrainsStore.deleteGrain(
          learningGrain.value.id,
          learningModule.value.id,
          props.learningCourseId
        )
        fieldToUpdate.value = null
        emits('delete')
      } catch (error) {
        console.error('Error deleting grain:', error)
      }
    }
  })
}

// HAPPENS ON MOUNT
onMounted(() => {})

// WATCHERS
</script>

<template>
  <div
    class="rounded-border border-surface-300 dark:border-surface-500 text-muted-color relative mt-4 block w-full border border-dashed p-4"
    :class="[{ 'bg-blue-50': isEditing }, { 'p-input-filled': !isEditing }]"
  >
    <div class="mb-2 w-full items-center" :class="[{ flex: !isEditing }]">
      <template v-if="!isEditing">
        <h4 class="text-color block font-bold">{{ learningGrain.title }}</h4>

        <p-badge
          class="ml-auto"
          :value="LearningStatus.label(learningGrain.status)"
          :severity="LearningStatus.color(learningGrain.status)"
        />
        <p-button
          text
          icon="pi pi-pencil"
          class="ml-2"
          severity="secondary"
          :disabled="!canEditGrain(learningGrain)"
          @click="startGrainUpdate"
        />
        <p-button
          text
          icon="pi pi-trash"
          severity="secondary"
          :disabled="!canDeleteGrain(learningGrain)"
          @click="deleteGrain"
        />
      </template>
      <template v-else>
        <h6>Titre du grain</h6>
        <p-input-text class="mb-4 w-full" :disabled="!isEditing" v-model="learningGrain.title" />
      </template>
    </div>

    <div>
      <h6>Nombre de point</h6>
      <p-input-number
        class="mb-4 w-full"
        :disabled="!isEditing"
        v-model="learningGrain.points"
        @update="$emit('update', learningGrain)"
      />
    </div>

    <div class="grid-nogutter grid grid-cols-12 gap-4">
      <div class="col-span-12 flex items-center">
        <h6>Quiz de début de grain</h6>

        <!-- Versions du quiz pré -->
        <div v-if="learningGrain.quizPre" class="ml-2 flex items-center gap-2">
          <c-quiz-version-tag
            v-if="learningGrain.quizPre.activeVersion"
            :version="learningGrain.quizPre.activeVersion"
            severity="success"
            clickable
            show-question-count
            @click="editQuiz('pre', learningGrain.quizPre.activeVersion?.id)"
          />

          <c-quiz-version-tag
            v-if="learningGrain.quizPre.latestVersion?.status === LearningStatus.Values.DRAFT"
            :version="learningGrain.quizPre.latestVersion"
            severity="warn"
            clickable
            show-question-count
            @click="editQuiz('pre', learningGrain.quizPre.latestVersion?.id)"
          />
        </div>

        <p-button
          class="ml-auto"
          label="Editer le quiz"
          @click="editQuiz('pre', learningGrain.quizPre?.latestVersion?.id)"
          outlined
          :disabled="!learningGrain.quizPre"
        />
      </div>
      <div class="col-span-12 flex items-center">
        <h6>Quiz de fin de grain</h6>

        <!-- Versions du quiz post -->
        <div v-if="learningGrain.quizPost" class="ml-2 flex items-center gap-2">
          <c-quiz-version-tag
            v-if="learningGrain.quizPost.activeVersion"
            :version="learningGrain.quizPost.activeVersion"
            severity="success"
            clickable
            show-question-count
            @click="editQuiz('post', learningGrain.quizPost.activeVersion?.id)"
          />

          <c-quiz-version-tag
            v-if="learningGrain.quizPost.latestVersion?.status === LearningStatus.Values.DRAFT"
            :version="learningGrain.quizPost.latestVersion"
            severity="warn"
            clickable
            show-question-count
            @click="editQuiz('post', learningGrain.quizPost.latestVersion?.id)"
          />
        </div>

        <p-button
          class="ml-auto"
          label="Editer le quiz"
          @click="editQuiz('post', learningGrain.quizPost?.latestVersion?.id)"
          outlined
          :disabled="!learningGrain.quizPost"
        />
      </div>
    </div>

    <CLearningModuleGrainSupportList
      :is-editing="isEditing"
      :add-support="() => learningGrain.addNewSupport()"
      :learning-course-id="props.learningCourseId"
      :learning-module-id="learningModule.id"
      v-model:learning-grain="learningGrain"
    />

    <div v-if="isEditing" class="mt-8 flex w-full justify-between">
      <p-button severity="secondary" label="Annuler" @click="$emit('cancel')" />
      <p-button
        label="Valider"
        :loading="isLoading"
        :disabled="learningGrain.hasInvalidSupports() || loading.uploadSupportFile"
        @click="() => validate()"
      />
    </div>
  </div>

  <p-message
    class="mt-2 mb-0"
    :pt="{ wrapper: { class: 'py-1' } }"
    severity="warn"
    v-for="(text, i) in missingQuizzesTextList"
    :key="`block_${i}`"
    ><p>{{ text }}</p>
  </p-message>

  <p-button
    label="Publier"
    class="mt-4 ml-auto"
    :disabled="!learningGrain.canBePublished || !canPublishGrain(learningGrain)"
    @click="publishGrain"
  />
</template>
